import * as React from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";

import { FleetHostPoint } from "api/fleet";
import { formatTimeOfDay } from "../format";

// Token colors, so the chart follows light and dark mode. CPU and memory keep these colors on the
// whole dashboard: the meters above the chart, the legend and the lines.
export const LOAD_SERIES = [
  { key: "cpuPct", label: "CPU", color: "rgb(var(--chart-1))" },
  { key: "memUsedPct", label: "Memory", color: "rgb(var(--chart-2))" },
] as const;

const AXIS_TICK = { fontSize: 12, fill: "rgb(var(--muted-foreground))" };
const GRID = "rgb(var(--border))";

/** Legend with line keys, mirroring the 2px lines. */
export const LoadLegend: React.FC = () => (
  <ul className="flex items-center gap-4 text-xs text-muted-foreground">
    {LOAD_SERIES.map((series) => (
      <li key={series.key} className="inline-flex items-center gap-1.5">
        <span
          aria-hidden="true"
          className="h-0.5 w-3 rounded-full"
          style={{ background: series.color }}
        />
        {series.label}
      </li>
    ))}
  </ul>
);

// One readout for both series at the hovered time: the value leads, the series name follows.
const LoadTooltip: React.FC<TooltipProps<number, string>> = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }
  return (
    <div className="rounded-control border border-border bg-surface-raised px-3 py-2 text-xs shadow-popover">
      <p className="mb-1.5 text-muted-foreground">{formatTimeOfDay(String(label))} IST</p>
      <ul className="space-y-1">
        {LOAD_SERIES.map((series) => {
          const entry = payload.find((item) => item.dataKey === series.key);
          const value = entry && typeof entry.value === "number" ? entry.value : null;
          return (
            <li key={series.key} className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className="h-0.5 w-3 rounded-full"
                style={{ background: series.color }}
              />
              <span className="font-semibold tabular-nums text-foreground">
                {value === null ? "—" : `${value.toFixed(1)}%`}
              </span>
              <span className="text-muted-foreground">{series.label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

/** Host CPU and memory from the sampler's points (oldest first), on one 0–100% axis. */
const LoadChart: React.FC<{ points: FleetHostPoint[] }> = ({ points }) => (
  <div className="h-48 w-full">
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: -4 }}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis
          dataKey="at"
          tickFormatter={formatTimeOfDay}
          tick={AXIS_TICK}
          tickLine={false}
          axisLine={{ stroke: GRID }}
          minTickGap={40}
        />
        <YAxis
          domain={[0, 100]}
          ticks={[0, 50, 100]}
          tick={AXIS_TICK}
          tickLine={false}
          axisLine={false}
          unit="%"
          width={48}
        />
        <Tooltip
          content={<LoadTooltip />}
          cursor={{ stroke: "rgb(var(--subtle-foreground))", strokeWidth: 1 }}
          isAnimationActive={false}
        />
        {LOAD_SERIES.map((series) => (
          <Line
            key={series.key}
            type="monotone"
            dataKey={series.key}
            name={series.label}
            stroke={series.color}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            dot={false}
            activeDot={{ r: 4, strokeWidth: 2, stroke: "rgb(var(--surface))", fill: series.color }}
            connectNulls={false}
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  </div>
);

export default LoadChart;
